import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Settings as SettingsIcon } from 'lucide-react';
import { ConfirmationDialog } from '../components/ui/ConfirmationDialog';

const changePasswordSchema = z.object({
  currentPassword: z.string().min(6, 'Password must be at least 6 characters'),
  newPassword: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string().min(6, 'Password must be at least 6 characters'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

function Settings() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);

  const { register, handleSubmit, formState: { errors }, reset } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
  });

  // Check if user is logged in with Google
  useEffect(() => {
    const checkAuthProvider = async () => {
      if (!user) return;
      
      const { data, error } = await supabase.auth.getUser();
      
      if (error) {
        console.error('Error getting user:', error);
        return;
      }
      
      // Check if the user has Google as an identity provider
      const isGoogle = data.user?.app_metadata?.provider === 'google' || 
                       data.user?.identities?.some(identity => identity.provider === 'google');
      
      setIsGoogleUser(!!isGoogle);
    };
    
    checkAuthProvider();
  }, [user]);

  const onChangePassword = async (data: ChangePasswordFormData) => {
    if (isSubmittingPassword) return; // Prevent multiple submissions
    
    try {
      setIsSubmittingPassword(true);
      setError('');
      setSuccess('');

      // First verify the current password by trying to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: data.currentPassword,
      });

      if (signInError) {
        setError('Current password is incorrect');
        return;
      }

      // Update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: data.newPassword,
      });

      if (updateError) throw updateError;

      setSuccess('Password updated successfully');
      reset();
    } catch (err) {
      console.error('Error updating password:', err);
      setError('Failed to update password. Please try again.');
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    try {
      setIsDeleting(true);
      setError('');

      // Start a Postgres transaction to handle all deletions
      const { error: deleteError } = await supabase.rpc('delete_user_account', {
        user_id: user.id
      });

      if (deleteError) throw deleteError;

      // Delete the auth user
      const { error: authError } = await supabase.auth.admin.deleteUser(
        user.id
      );

      if (authError) throw authError;

      // Sign out the user
      await signOut();
      navigate('/');
    } catch (err) {
      console.error('Error deleting account:', err);
      setError('Failed to delete account. Please try again.');
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] space-y-8 flex-col flex items-center justify-center px-6 pb-8 md:py-24">
      <div className="panel w-full max-w-md">
        <h1 className="font-tilt-warp text-3xl text-light text-center">Settings</h1>

        {error && (
          <div className="bg-red-500/10 text-red-500 p-4 rounded-lg text-center">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-500/10 text-green-500 p-4 rounded-lg text-center">
            {success}
          </div>
        )}

        {isGoogleUser ? (
          <div className="mt-6 p-4 bg-gray-custom/10 rounded-lg">
            <p className="text-light/60 text-center">
              Password management is handled by Google for your account.
              To change your password, please visit your Google account settings.
            </p>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-semibold mb-6 mt-6">Change Password</h2>
            <form onSubmit={handleSubmit(onChangePassword)} className="space-y-6">
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-light/80 mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  {...register('currentPassword')}
                  className="block w-full h-12 rounded-lg bg-dark border border-gray-custom/20 px-4 text-light focus:border-secondary/40 focus:outline-none"
                />
                {errors.currentPassword && (
                  <p className="mt-1 text-sm text-red-500">{errors.currentPassword.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-light/80 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  {...register('newPassword')}
                  className="block w-full h-12 rounded-lg bg-dark border border-gray-custom/20 px-4 text-light focus:border-secondary/40 focus:outline-none"
                />
                {errors.newPassword && (
                  <p className="mt-1 text-sm text-red-500">{errors.newPassword.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-light/80 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  {...register('confirmPassword')}
                  className="block w-full h-12 rounded-lg bg-dark border border-gray-custom/20 px-4 text-light focus:border-secondary/40 focus:outline-none"
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-500">{errors.confirmPassword.message}</p>
                )}
              </div>

              <button type="submit" className="btn-secondary w-full h-12">
                {isSubmittingPassword ? t('common.saving') : 'Update Password'}
              </button>
            </form>
          </>
        )}
      </div>

      <div className="panel w-full max-w-md">
        <h2 className="text-xl font-semibold text-red-500 mb-6">Delete Account</h2>
        <p className="text-light/60 mb-6">
          Once you delete your account, there is no going back. Please be certain.
        </p>

        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="btn w-full h-12 bg-red-500/10 text-red-500 hover:bg-red-500/20"
          disabled={isDeleting}
        >
          {isDeleting ? 'Deleting...' : 'Delete Account'}
        </button>
      </div>

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteAccount}
        title="Delete Account"
        message={
          <div className="space-y-4 text-light/60">
            <p>Are you sure you want to delete your account?</p>
            <p>This action cannot be undone and will:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Delete your profile permanently</li>
              <li>Remove you from all bands</li>
              <li>Delete bands where you're the last member</li>
              <li>Remove all your stories and events</li>
            </ul>
          </div>
        }
        confirmLabel="Delete Account"
        isDanger={true}
        isLoading={isDeleting}
      />
    </div>
  );
}

export default Settings;